import React, { useEffect, useState } from "react";
import { Container, Table, Button, Form, Alert, Spinner, Modal } from "react-bootstrap";
import { Plus, Trash, Pencil } from "react-bootstrap-icons";
import UserService from "../../../services/user.service";

/**
 * Super-admin page to curate the list of user titles used in add/edit user selects.
 */
const AdminUserTitles = () => {
  const [titles, setTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editValue, setEditValue] = useState("");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await UserService.getAllCuratedTitles();
      setTitles(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || err.message || "Failed to load titles");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    const title = newTitle.trim();
    if (!title) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await UserService.createCuratedTitle(title);
      setNewTitle("");
      setSuccess("Title added");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to add title");
    }
    setSaving(false);
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Remove title "${row.title}" from the curated list?`)) return;
    setError(null);
    setSuccess(null);
    try {
      await UserService.deleteCuratedTitle(row.id);
      setSuccess("Title removed");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to remove title");
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditValue(row.title);
  };

  const saveEdit = async () => {
    if (!editRow) return;
    const title = editValue.trim();
    if (!title) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await UserService.updateCuratedTitle(editRow.id, { title });
      setEditRow(null);
      setSuccess("Title updated");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to update title");
    }
    setSaving(false);
  };

  return (
    <Container className="py-3">
      <h2>User Titles</h2>
      <p className="text-muted">
        Curate the titles available when adding or editing users. Site admins pick from this list.
      </p>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      <Form onSubmit={handleAdd} className="mb-4 d-flex gap-2 align-items-end" style={{ maxWidth: 480 }}>
        <Form.Group className="flex-grow-1" controlId="newTitle">
          <Form.Label>Add title</Form.Label>
          <Form.Control
            type="text"
            maxLength={200}
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="e.g. Manager"
          />
        </Form.Group>
        <Button type="submit" disabled={saving || !newTitle.trim()}>
          <Plus /> Add
        </Button>
      </Form>

      {loading ? (
        <Spinner animation="border" />
      ) : (
        <Table striped bordered hover size="sm" style={{ maxWidth: 640 }}>
          <thead>
            <tr>
              <th>Title</th>
              <th style={{ width: 140 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {titles.length === 0 && (
              <tr>
                <td colSpan={2} className="text-muted">No titles yet. Add one above.</td>
              </tr>
            )}
            {titles.map((row) => (
              <tr key={row.id}>
                <td>{row.title}</td>
                <td>
                  <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => openEdit(row)}>
                    <Pencil /> Edit
                  </Button>
                  <Button size="sm" variant="outline-danger" onClick={() => handleDelete(row)}>
                    <Trash /> Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={!!editRow} onHide={() => setEditRow(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Title</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            type="text"
            maxLength={200}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditRow(null)}>Cancel</Button>
          <Button variant="primary" disabled={saving || !editValue.trim()} onClick={saveEdit}>Save</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminUserTitles;
